import React, { useState, useEffect } from 'react';
import { db, auth } from '../../Services/firebase-config';
import { collection, getDocs, doc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';

import { jsPDF } from 'jspdf';
import Papa from 'papaparse';
import autoTable from 'jspdf-autotable';
const formatNumberWithCommas = (value) => {
  if (value === null || value === undefined || value === '') return '0';
  const number = parseFloat(value);
  if (isNaN(number)) {
    return value;
  }
  return number.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

const EnterpriseAnalysis = () => {
  const [enterprises, setEnterprises] = useState([]);
  const [userUid, setUserUid] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUserUid(user.uid);
      } else {
        setUserUid(null);
      }
    });
    return () => unsubscribeAuth();
  }, []);

  useEffect(() => {
    const fetchEnterprises = async () => {
      if (!userUid) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const enterprisesSnapshot = await getDocs(collection(db, 'users', userUid, 'kibalos'));
        const enterprisesData = await Promise.all(enterprisesSnapshot.docs.map(async (kibaloDoc) => {
          const data = kibaloDoc.data();

          // Calculate Total Cost (Input Cost / Year)
          const establishmentCost = data.establishmentCostPerYear || 0; // Annualized
          const depreciationCost = data.depreciationTable?.reduce((acc, item) => acc + (parseFloat(item.depreciationAmount) || 0), 0) || 0;
          const maintenanceCost = data.maintenanceTable?.reduce((acc, item) => acc + (parseFloat(item.amount) || 0), 0) || 0;
          const interestCost = data.interestTable?.reduce((acc, item) => acc + (parseFloat(item.amount) || 0), 0) || 0;
          const variableCost = data.variableCosts?.reduce((acc, item) => acc + (parseFloat(item.amount) || 0), 0) || 0;
          
          const enterpriseTotalCost = establishmentCost + depreciationCost + maintenanceCost + interestCost + variableCost;

          // Calculate Gross Revenue
          const grossRevenue = data.grossRevenue?.reduce((acc, item) => acc + (parseFloat(item.amount) || 0), 0) || 0;

          // Calculate Gross Profit (Profit / Year)
          const enterpriseGrossProfit = grossRevenue - enterpriseTotalCost;

          // Correctly calculate Startup Capital
          const totalEstablishmentCost = data.establishmentTable?.reduce((acc, item) => acc + (parseFloat(item.amount) || 0), 0) || 0;
          const totalDepreciationAssetsCost = data.depreciationTable?.reduce((acc, item) => acc + (parseFloat(item.quantity || '0') * parseFloat(item.unitCost || '0')), 0) || 0;
          
          const enterpriseStartupCapital = 
            totalEstablishmentCost + 
            totalDepreciationAssetsCost +
            maintenanceCost + // Other Annual Fixed Costs
            interestCost +
            (0.4 * variableCost);

          return {
            id: kibaloDoc.id,
            name: data.name,
            totalCost: enterpriseTotalCost,
            grossProfit: enterpriseGrossProfit,
            startupCapital: enterpriseStartupCapital,
          };
        }));

        // Calculate the rank based on your formula: (Gross Profit / Total Cost) * 1,000,000
        const rankedEnterprises = enterprisesData.map(enterprise => {
          const rankValue = enterprise.totalCost !== 0 ? (enterprise.grossProfit / enterprise.totalCost) * 1000000 : 0;
          return { ...enterprise, rankValue: isFinite(rankValue) ? rankValue : 0 };
        }).sort((a, b) => {
          const rankA = a.rankValue;
          const rankB = b.rankValue;

          // Handle cases where rankValue might be NaN or Infinity
          if (!isFinite(rankB)) return -1;
          if (!isFinite(rankA)) return 1;

          return rankB - rankA;
        }); // Sort in descending order

        setEnterprises(rankedEnterprises);
      } catch (err) {
        console.error("Error fetching enterprise analysis data:", err);
        setError("Failed to load enterprise analysis. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchEnterprises();
  }, [userUid]);

  const generatePDF = () => {
    const doc = new jsPDF();
    doc.text("Enterprise Analysis Report", 14, 16);

    const tableColumn = ["Rank", "Enterprise Name", "Input Cost / Year (Ugx)", "Profit / Year (Ugx)", "Profit / Million Invested (Ugx)", "Startup Capital (Ugx)"]; // prettier-ignore
    const tableRows = [];

    enterprises.forEach((enterprise, index) => {
      const enterpriseData = [
        index + 1,
        enterprise.name,
        formatNumberWithCommas(enterprise.totalCost),
        formatNumberWithCommas(enterprise.grossProfit),
        enterprise.rankValue ? formatNumberWithCommas(enterprise.rankValue) : 'N/A',
        formatNumberWithCommas(enterprise.startupCapital)
      ];
      tableRows.push(enterpriseData);
    });

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 20,
      theme: 'grid'
    });

    doc.save('enterprise-analysis.pdf');
  };

  const generateExcel = () => {
    const dataForExcel = enterprises.map((enterprise, index) => ({
      'Rank': index + 1,
      'Enterprise Name': enterprise.name,
      'Input Cost / Year (Ugx)': enterprise.totalCost,
      'Profit / Year (Ugx)': enterprise.grossProfit,
      'Profit / Million Invested (Ugx)': enterprise.rankValue,
      'Startup Capital (Ugx)': enterprise.startupCapital,
    }));

    const csv = Papa.unparse(dataForExcel);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.href) {
      URL.revokeObjectURL(link.href);
    }
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.setAttribute('download', 'enterprise-analysis.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return <div className="p-6 text-center">Loading enterprise analysis...</div>;
  }

  if (error) {
    return <div className="p-6 text-center text-red-500">{error}</div>;
  }

  if (enterprises.length === 0) {
    return <div className="p-6 text-center">No enterprises to analyze.</div>;
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold">ENTERPRISE ANALYSIS</h1>
        <div className="flex gap-2">
          <button onClick={generatePDF} className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">
            Download as PDF
          </button>
          <button onClick={generateExcel} className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700">
            Download as Excel
          </button>
        </div>
      </div>
      <table className="w-full border-collapse border border-gray-300">
        <thead>
          <tr className="bg-gray-200">
            <th className="border border-gray-300 px-4 py-2">Rank</th>
            <th className="border border-gray-300 px-4 py-2">Enterprise Name</th>
            <th className="border border-gray-300 px-4 py-2">Input Cost / Year (Ugx)</th>
            <th className="border border-gray-300 px-4 py-2">Profit / Year (Ugx)</th>
            <th className="border border-gray-300 px-4 py-2">Profit / Million Invested (Ugx)</th>
            <th className="border border-gray-300 px-4 py-2">Startup Capital (Ugx)</th>
          </tr>
        </thead>
        <tbody>
          {enterprises.map((enterprise, index) => (
            <tr key={enterprise.id}>
              <td className="border border-gray-300 px-4 py-2">{index + 1}</td>
              <td className="border border-gray-300 px-4 py-2">{enterprise.name}</td>
              <td className="border border-gray-300 px-4 py-2">{formatNumberWithCommas(enterprise.totalCost)}</td>
              <td className="border border-gray-300 px-4 py-2">{formatNumberWithCommas(enterprise.grossProfit)}</td>
              <td className="border border-gray-300 px-4 py-2">{enterprise.rankValue ? formatNumberWithCommas(enterprise.rankValue) : 'N/A'}</td>
              <td className="border border-gray-300 px-4 py-2">{formatNumberWithCommas(enterprise.startupCapital)}</td>
            </tr>
          ))}
        </tbody>
      </table>

    
    </div>
  );
};

export default EnterpriseAnalysis;